import { DocumentNode } from "graphql";
import { ComponentProps } from "react";

import { type TableDialog } from "@/components/organisms/Table/TableDialog";
import { Mutations } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";

/**
 * Generates properties for a delete node button.
 *
 * @param {Object} params - The parameters for the function.
 * @param {DocumentNode[]} params.refetchQueries - An array of GraphQL document nodes to refetch after mutation.
 * @returns {Object} The properties for the delete node button.
 * @returns {string} returns.children - The text to display on the button.
 * @returns {string} returns.variant - The variant style of the button.
 * @returns {string} returns.color - The color style of the button.
 * @returns {function} returns.onClick - The click handler for the button, which triggers the delete mutation.
 */
const getDeleteNodeButtonProps = ({
  refetchQueries,
}: {
  refetchQueries: DocumentNode[];
}) => {
  const { mutate } = useBackendMutation({
    mutation: Mutations.DELETE_NODES,
    refetchQueries: refetchQueries,
  });
  return {
    variant: "destructive" as const,
    children: "Delete",

    onClick: (checkedRows) => {
      mutate({
        variables: {
          input: {
            ids: checkedRows,
          },
        },
      });
    },
  } as ComponentProps<typeof TableDialog>["buttons"][number];
};

export const Setup = {
  getDeleteNodeButtonProps,
} as const;
